'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';
import { X, Menu } from 'lucide-react';

interface Announcement {
  id: string;
  message: string;
  link_url?: string;
  link_text?: string;
}

export default function PublicNav() {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [announcementDismissed, setAnnouncementDismissed] = useState(false);
  const [cookieChoice, setCookieChoice] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem('incade_announcement_dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedAt < sevenDays) {
        setAnnouncementDismissed(true);
      }
    }

    const cookieVal = localStorage.getItem('incade_cookies_accepted');
    if (cookieVal !== null) {
      setCookieChoice(cookieVal);
    }

    const fetchAnnouncement = async () => {
      const supabase = createClient();
      const now = new Date().toISOString();
      const { data } = await supabase
        .from('announcements')
        .select('id, message, link_url, link_text')
        .eq('is_active', true)
        .lte('starts_at', now)
        .gte('ends_at', now)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (data) setAnnouncement(data);
    };
    fetchAnnouncement();
  }, []);

  const dismissAnnouncement = () => {
    localStorage.setItem('incade_announcement_dismissed', Date.now().toString());
    setAnnouncementDismissed(true);
  };

  const acceptCookies = () => {
    localStorage.setItem('incade_cookies_accepted', 'true');
    setCookieChoice('true');
  };

  const rejectCookies = () => {
    localStorage.setItem('incade_cookies_accepted', 'false');
    setCookieChoice('false');
  };

  const showAnnouncement = announcement && !announcementDismissed;

  const navLinks = [
    { href: '/features', label: 'Features' },
    { href: '/pricing', label: 'Pricing' },
    { href: '/blog', label: 'Blog' },
    { href: '/webinars', label: 'Webinars' },
    { href: '/careers', label: 'Careers' },
    { href: '/help', label: 'Help' },
  ];

  return (
    <>
      {showAnnouncement && (
        <div
          style={{ background: 'rgba(249,115,22,0.08)', borderBottom: '1px solid rgba(249,115,22,0.18)' }}
          className="text-[#FB923C] text-xs py-2 px-4 flex items-center justify-center gap-3 relative font-semibold"
        >
          <span>
            {announcement.message}
            {announcement.link_url && announcement.link_text && (
              <a href={announcement.link_url} className="ml-2 underline hover:text-orange-300 transition-colors">
                {announcement.link_text}
              </a>
            )}
          </span>
          <button
            onClick={dismissAnnouncement}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-[#FB923C] hover:text-orange-200 transition-colors"
            aria-label="Dismiss announcement"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <nav
        style={{
          height: '62px',
          background: 'rgba(6,11,20,0.85)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
        className="sticky top-0 z-[99]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex items-center justify-between h-full">
            <Link href="/" className="flex items-center gap-2 flex-shrink-0">
              <AppLogo size={32} />
              <span className="font-bold text-white text-lg tracking-tight">Incade</span>
              <span className="text-[#F97316] text-sm font-mono font-semibold">.io</span>
            </Link>

            <div className="hidden md:flex items-center gap-6">
              {navLinks.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="text-[13px] font-[500] text-[#64748B] hover:text-white transition-colors duration-200"
                >
                  {item.label}
                </Link>
              ))}
            </div>

            <div className="hidden md:flex items-center gap-3">
              <Link
                href="/login"
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#94A3B8',
                  padding: '7px 18px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                }}
                className="hover:border-[rgba(249,115,22,0.3)] hover:text-white hover:bg-[rgba(249,115,22,0.1)]"
              >
                Login
              </Link>
              <Link
                href="/start"
                style={{
                  background: 'linear-gradient(135deg,#F97316,#EA580C)',
                  color: 'white',
                  padding: '7px 18px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 700,
                  transition: 'all 0.2s ease',
                }}
                className="hover:opacity-90 hover:-translate-y-px"
              >
                Start Free Trial
              </Link>
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded-lg text-[#64748B] hover:text-white transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div
            style={{ background: '#060B14', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
            className="md:hidden py-4 space-y-1 px-4"
          >
            {navLinks.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2.5 text-[13px] font-[500] text-[#64748B] hover:text-white rounded-lg transition-colors"
              >
                {item.label}
              </Link>
            ))}
            <div className="pt-3 flex flex-col gap-2 px-1">
              <Link
                href="/login"
                onClick={() => setMobileOpen(false)}
                style={{ border: '1px solid rgba(255,255,255,0.1)', color: '#94A3B8', borderRadius: '8px', fontSize: '13px', fontWeight: 600 }}
                className="text-center py-2.5 px-4 block transition-colors hover:text-white"
              >
                Login
              </Link>
              <Link
                href="/start"
                onClick={() => setMobileOpen(false)}
                style={{ background: 'linear-gradient(135deg,#F97316,#EA580C)', color: 'white', borderRadius: '8px', fontSize: '13px', fontWeight: 700 }}
                className="text-center py-2.5 px-4 block"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
        )}
      </nav>

      {cookieChoice === null && (
        <div
          style={{ background: 'rgba(6,11,20,0.97)', border: '1px solid rgba(255,255,255,0.08)' }}
          className="fixed bottom-0 left-0 right-0 z-50 text-white p-4 shadow-2xl"
        >
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm text-[#94A3B8]">
                We use cookies to improve your experience. By continuing, you agree to our{' '}
                <Link href="/privacy-policy" className="underline text-[#F97316] hover:text-orange-300">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <button
                onClick={rejectCookies}
                style={{ border: '1px solid rgba(255,255,255,0.1)', color: '#94A3B8', borderRadius: '8px', fontSize: '13px' }}
                className="font-medium px-4 py-2 hover:text-white transition-colors"
              >
                Reject All
              </button>
              <button
                onClick={acceptCookies}
                style={{ background: 'linear-gradient(135deg,#F97316,#EA580C)', color: 'white', borderRadius: '8px', fontSize: '13px', fontWeight: 700 }}
                className="px-4 py-2"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
